import { LocalService } from '@app/@core/services/local.service';
import { CommonService } from '@app/shared/services/common.service';
import { ROLE_ID } from '@app/shared/constants/rolesConstant.model';

export type Br0036DeleteEligibilityReason =
  | 'nde_admin'
  | 'no_rule_hours'
  | 'no_upload_time'
  | 'outside_window'
  | 'allowed';

export interface Br0036DeleteEligibilityExplanation {
  documentId: unknown;
  isNdeAdmin: boolean;
  documentDeletionStaffHours: number | null;
  loginRoleId: number | null;
  uploadCandidates: Record<string, unknown>;
  uploadMs: number | null;
  uploadedAtUtc: string | null;
  ageHours: number | null;
  ageMinutes: number | null;
  withinWindow: boolean;
  canShowDelete: boolean;
  reason: Br0036DeleteEligibilityReason;
}

/** Parsed hours from GET business-rules/fetch-buisness-rules-by-key response (nested shapes vary). */
export function parseDocumentDeletionStaffHoursFromApi(response: unknown): number | null {
  const r = response as Record<string, unknown> | null | undefined;
  const data = r?.data as Record<string, unknown> | undefined;
  const inner = data?.data as Record<string, unknown> | undefined;
  const categoryData =
    (data?.category_data as Record<string, unknown> | undefined) ??
    (data?.objCategoryData as Record<string, unknown> | undefined) ??
    (inner?.category_data as Record<string, unknown> | undefined);
  const raw =
    inner?.intValue ??
    data?.intValue ??
    r?.intValue ??
    categoryData?.intValue ??
    (categoryData as Record<string, unknown> | undefined)?.int_value;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function resolveLoginRoleId(localService: LocalService, commonService: CommonService): number | null {
  const tryParse = (v: unknown): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  let roleId = tryParse(localService.getItem('role_id'));
  if (roleId != null) return roleId;

  if (typeof sessionStorage !== 'undefined') {
    roleId = tryParse(sessionStorage.getItem('role_id'));
    if (roleId != null) return roleId;
  }

  try {
    const state = commonService.getCurrentApplicationState();
    roleId = tryParse(state?.role_id);
    if (roleId != null) return roleId;
  } catch {
    /* ignore */
  }

  try {
    const userRaw = localService.getItem('user');
    const user =
      typeof userRaw === 'string' && userRaw.trim() !== '' ? JSON.parse(userRaw) : userRaw;
    if (user && typeof user === 'object') {
      const role =
        (user as any).role_id ??
        (user as any).roleId ??
        (user as any).role?.id ??
        (user as any).role?.role_id ??
        (user as any).userroles?.[0]?.id ??
        (user as any).user_role_id ??
        (user as any).userRoleId;
      roleId = tryParse(role);
      if (roleId != null) return roleId;
    }
  } catch {
    return null;
  }

  return null;
}

export function isNdeAdministratorForDocumentDeletion(roleId: number | null): boolean {
  return roleId === ROLE_ID.NDE_Administrator;
}

/** Naive Postgres/API ISO without offset — stored and sent as UTC wall clock. */
const NAIVE_UTC_TIMESTAMP_RE =
  /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(\.\d{1,6})?$/;

/**
 * Parse dashboard/DB upload timestamps for BR0036 age checks.
 * Client upload uses toISOString(); DB column is timestamptz (or legacy timestamp as UTC).
 */
export function parseUploadValueToUtcMs(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : t;
  }
  const s = String(value).trim();
  if (!s) {
    return null;
  }
  if (NAIVE_UTC_TIMESTAMP_RE.test(s)) {
    const t = new Date(`${s.replace(' ', 'T')}Z`).getTime();
    return Number.isNaN(t) ? null : t;
  }
  const usMs = parseUsDisplayDateToMs(s);
  if (usMs != null) {
    return usMs;
  }
  const t = new Date(s).getTime();
  return Number.isNaN(t) ? null : t;
}

function parseUsDisplayDateToMs(value: string): number | null {
  const m = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) {
    return null;
  }
  const month = parseInt(m[1], 10);
  const day = parseInt(m[2], 10);
  const year = parseInt(m[3], 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }
  const d = new Date(year, month - 1, day);
  const t = d.getTime();
  if (Number.isNaN(t)) {
    return null;
  }
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
    return null;
  }
  return t;
}

function parseUploadValueToMs(value: unknown): number | null {
  return parseUploadValueToUtcMs(value);
}

/** Collect upload timestamp candidates (for eligibility explanation objects). */
export function collectUploadCandidatesFromEducatorDocumentRow(row: any): Record<string, unknown> {
  const raw = row?._raw ?? row;
  return {
    _uploadMs: row?._uploadMs ?? null,
    row_uploaded_at: row?.uploaded_at ?? row?.uploadedAt ?? null,
    row_uploaded_date: row?.uploaded_date ?? row?.uploadedDate ?? null,
    raw_uploaded_at: raw?.uploaded_at ?? raw?.uploadedAt ?? null,
    raw_uploaded_date: raw?.uploaded_date ?? raw?.uploadedDate ?? null
  };
}

/**
 * Prefer machine timestamps (`_uploadMs`, `uploaded_at`) over grid display `uploaded_date` (MM/DD/YYYY).
 */
export function uploadTimeMsFromEducatorDocumentRow(row: any): number | null {
  const raw = row?._raw ?? row;
  const candidates: unknown[] = [
    row?._uploadMs,
    row?.uploaded_at,
    row?.uploadedAt,
    raw?.uploaded_at,
    raw?.uploadedAt,
    raw?.uploaded_date,
    raw?.uploadedDate,
    row?.uploaded_date,
    row?.uploadedDate
  ];
  for (const c of candidates) {
    const ms = parseUploadValueToMs(c);
    if (ms != null) {
      return ms;
    }
  }
  return null;
}

export function explainCanShowDeleteEducatorDocument(
  row: any,
  documentDeletionStaffHours: number | null,
  loginRoleId: number | null
): Br0036DeleteEligibilityExplanation {
  const uploadCandidates = collectUploadCandidatesFromEducatorDocumentRow(row);
  const isNdeAdmin = isNdeAdministratorForDocumentDeletion(loginRoleId);

  if (isNdeAdmin) {
    return {
      documentId: row?.document_id ?? row?.id,
      isNdeAdmin: true,
      documentDeletionStaffHours,
      loginRoleId,
      uploadCandidates,
      uploadMs: uploadTimeMsFromEducatorDocumentRow(row),
      uploadedAtUtc: null,
      ageHours: null,
      ageMinutes: null,
      withinWindow: true,
      canShowDelete: true,
      reason: 'nde_admin'
    };
  }

  if (documentDeletionStaffHours == null || documentDeletionStaffHours <= 0) {
    return {
      documentId: row?.document_id ?? row?.id,
      isNdeAdmin: false,
      documentDeletionStaffHours,
      loginRoleId,
      uploadCandidates,
      uploadMs: null,
      uploadedAtUtc: null,
      ageHours: null,
      ageMinutes: null,
      withinWindow: false,
      canShowDelete: false,
      reason: 'no_rule_hours'
    };
  }

  const uploadMs = uploadTimeMsFromEducatorDocumentRow(row);
  if (uploadMs == null) {
    return {
      documentId: row?.document_id ?? row?.id,
      isNdeAdmin: false,
      documentDeletionStaffHours,
      loginRoleId,
      uploadCandidates,
      uploadMs: null,
      uploadedAtUtc: null,
      ageHours: null,
      ageMinutes: null,
      withinWindow: false,
      canShowDelete: false,
      reason: 'no_upload_time'
    };
  }

  const ageMs = Date.now() - uploadMs;
  const ageHours = ageMs / (60 * 60 * 1000);
  const ageMinutes = ageMs / (60 * 1000);
  const limitMs = documentDeletionStaffHours * 60 * 60 * 1000;
  const withinWindow = ageMs <= limitMs;

  return {
    documentId: row?.document_id ?? row?.id,
    isNdeAdmin: false,
    documentDeletionStaffHours,
    loginRoleId,
    uploadCandidates,
    uploadMs,
    uploadedAtUtc: new Date(uploadMs).toISOString(),
    ageHours,
    ageMinutes,
    withinWindow,
    canShowDelete: withinWindow,
    reason: withinWindow ? 'allowed' : 'outside_window'
  };
}

/**
 * BR0036: staff may delete only within `documentDeletionStaffHours` of upload.
 * Only NDE_Administrator may delete after the window.
 */
export function canShowDeleteEducatorDocument(
  row: any,
  documentDeletionStaffHours: number | null,
  loginRoleId: number | null
): boolean {
  return explainCanShowDeleteEducatorDocument(row, documentDeletionStaffHours, loginRoleId).canShowDelete;
}
